// src/utils/cuadrePDF.js
import autoTable from 'jspdf-autotable';
import { n, toMoney } from '../utils/numbers';

/* ===== Métricas de efectivo manual ===== */
export const calcCuadreMetrics = (c) => {
  const t=c.totales||{}, a=c.arqueo?.[0]||{}, v=c.cierre?.[0]||{};
  // El conteo físico se muestra entero; el disponible para liquidar descuenta el fondo.
  const fondo=!!t.modoFondo;
  const conteoFisico=t.efectivoContadoCaja!=null||t.efectivoContado!=null;
  const contado=conteoFisico?n(t.efectivoContadoCaja??t.efectivoContado):fondo?n(t.efectivoEsperado):n(a.efectivoContado??a.efectivoManual);
  const inicial=n(c.efectivoInicial??t.efectivoInicial);
  const cierreEf=n(v.efectivo), cierreTar=n(v.tarjeta), pedidosYaMonto=n(v.pedidosYa);
  // Cuadres nuevos: se usan los cálculos del servidor (solo gastos en efectivo salen de caja).
  if (t.salidasEfectivo != null && 'efectivoEsperado' in t) {
    const gastosEf=n(t.gastosEfectivo ?? t.totalGastos), pagosEf=n(t.pagosEfectivo);
    return {totalArqueoEfectivo:contado,cierreEf,cierreTar,pedidosYaMonto,gastos:gastosEf,pagos:pagosEf,salidas:n(t.salidasEfectivo),inicial,
      esperado:t.efectivoEsperado==null?null:n(t.efectivoEsperado),diffEf:t.diferenciaCaja==null?null:n(t.diferenciaCaja),
      totalDepositar:n(t.efectivoLiquidar??contado)+n(c.faltantePagado),fondo,conteoFisico,dejaEnCaja:fondo?n(t.dejaEnCaja):null};
  }
  const gastos=(c.gastos||[]).reduce((sum,g)=>sum+n(g.cantidad),0);
  const pagos=n(t.pagosEfectivo), salidas=gastos+pagos;
  const esperado=inicial+cierreEf-salidas, diffEf=contado-esperado;
  return {totalArqueoEfectivo:contado,cierreEf,cierreTar,pedidosYaMonto,
    gastos,pagos,salidas,inicial,esperado,diffEf,conteoFisico:true,totalDepositar:contado+n(c.faltantePagado)};
};

/* ===== Encabezado y footer ===== */
export const addHeader = (pdf, { title, fecha, sucursal, formatDate }) => {
  const w = pdf.internal.pageSize.getWidth();
  pdf.setFillColor(245, 248, 252);
  pdf.rect(0, 0, w, 66, 'F');

  const sucNom = (sucursal || '—').toString();
  const computedTitle = title ?? `Cuadre - ${sucNom}`;
  const fechaLabel = typeof formatDate === 'function' ? formatDate(fecha) : (fecha || '');

  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(33, 37, 41);
  pdf.setFontSize(18);
  pdf.text(computedTitle, 40, 32);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  pdf.setTextColor(90, 90, 90);
  if (fechaLabel) pdf.text(`Fecha: ${fechaLabel}`, 40, 50);
};

export const addFooterPageNumbers = (pdf) => {
  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    const w = pdf.internal.pageSize.getWidth();
    const h = pdf.internal.pageSize.getHeight();
    pdf.setFontSize(9);
    pdf.setTextColor(130, 130, 130);
    pdf.text(`Página ${i} de ${pageCount}`, w - 40, h - 12, { align: 'right' });
  }
};

export const getCuadreDownloadName = (fecha, formatDate) => {
  const fechaLabel = typeof formatDate === 'function' ? formatDate(fecha) : (fecha || '');
  const base = `Cuadre - ${fechaLabel}`.trim();
  return `${base}`.replace(/[^\w .-]/g, '_') + '.pdf';
};

/* ===== Render principal ===== */
export const renderCuadreSection = (pdf, c, sucursalNombre, formatDate, options = {}) => {


  // Encabezado
  addHeader(pdf, {
    fecha: c.fecha,
    sucursal: sucursalNombre || '—',
    formatDate,
  });

  let y = 76; // debajo del encabezado
  const width = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const bottomMargin = 30;

  /* === Leyenda superior derecha === */
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(90, 90, 90);
  pdf.text('Montos en Quetzales (Q)', width - 40, 68, { align: 'right' });
  pdf.setTextColor(33, 37, 41);

  /* === Efectivo declarado === */
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('Total de efectivo', 40, y);
  y += 6;

  const arqueo = c.arqueo || [{}];
  const esConteo = arqueo[0]?.modoEfectivo === 'contado' || c.totales?.efectivoContadoCaja != null || c.totales?.efectivoContado != null;
  const mArqEfTotal = n(arqueo[0]?.efectivoContado ?? arqueo[0]?.efectivoManual);
  autoTable(pdf, {
    startY: y,
    head: [[esConteo ? 'Efectivo contado en caja' : arqueo[0]?.modoEfectivo === 'ventas' ? 'Ventas en efectivo reportadas' : 'Efectivo reportado']],
    body: [[toMoney(mArqEfTotal)]],
    styles: { fontSize: 9, cellPadding: 3, lineWidth: 0.2, lineColor: [230, 236, 240] },
    headStyles: { fillColor: [13, 71, 161], textColor: 255 },
    columnStyles: { 0: { halign: 'right' } },
    theme: 'grid',
  });

  y = pdf.lastAutoTable.finalY + 10;

  /* === Cierre de sistema === */
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('Cierre de sistema', 40, y);
  y += 6;

  const cierre = c.cierre || [{}];
  const v=cierre[0]||{};
  const ef=n(v.efectivo),tar=n(v.tarjeta),py=n(v.pedidosYa);
  autoTable(pdf,{startY:y,head:[['Efectivo','Tarjeta','PedidosYa','Total ventas']],
    body:[[toMoney(ef),toMoney(tar),toMoney(py),toMoney(ef+tar+py)]],
    styles:{fontSize:9,cellPadding:3},headStyles:{fillColor:[25,118,210],textColor:255},theme:'grid'});

  y = pdf.lastAutoTable.finalY + 10;

  /* === Gastos (solo Descripción y Cantidad) === */
  const gastos = Array.isArray(c.gastos) ? c.gastos : [];
  const gastosRows = gastos.map((g) => [
    (g.descripcion || '').toString(),
    toMoney(n(g.cantidad)),
  ]);

  const totalGastos = gastos.reduce((s, g) => s + n(g.cantidad), 0);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('Gastos', 40, y);
  y += 6;

  autoTable(pdf, {
    startY: y,
    head: [['Descripción', 'Cantidad']],
    body: gastosRows.length ? gastosRows : [['—', toMoney(0)]],
    styles: { fontSize: 9, cellPadding: 3, lineWidth: 0.2, lineColor: [230, 236, 240] },
    headStyles: { fillColor: [21, 101, 192], textColor: 255 },
    columnStyles: { 1: { halign: 'right' } },
    theme: 'grid',
    foot: [[
      { content: 'Total', styles: { halign: 'right' } },
      { content: toMoney(totalGastos), styles: { halign: 'right' } }
    ]],
    footStyles: { fillColor: [236, 239, 241], textColor: [33, 37, 41] },
  });

  y = pdf.lastAutoTable.finalY + 12;

  /* === Resumen (igual que antes, pero m.diffEf ya es REAL) === */
  {
    const m = calcCuadreMetrics(c);

    const boxTop = y;
    const leftColX = 52;
    const rightColX = width / 2 + 10;
    const GAP = 16;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(33, 37, 41);
    pdf.text('Resumen', leftColX, boxTop + 18);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const drawLine = (x, yLine, label, value) => {
      pdf.setTextColor(90, 90, 90);
      pdf.text(label, x, yLine);
      pdf.setTextColor(33, 37, 41);
      pdf.text(value, x + 140, yLine);
    };

    let leftY=boxTop+36;
    pdf.setFont('helvetica','bold');pdf.text('Ventas / ingresos',leftColX,leftY);
    pdf.setFont('helvetica','normal');leftY+=GAP;
    drawLine(leftColX,leftY,'Efectivo',toMoney(m.cierreEf));leftY+=GAP;
    drawLine(leftColX,leftY,'Tarjeta',toMoney(m.cierreTar));leftY+=GAP;
    drawLine(leftColX,leftY,'PedidosYa',toMoney(m.pedidosYaMonto));leftY+=GAP;
    drawLine(leftColX,leftY,'Total ventas',toMoney(m.cierreEf+m.cierreTar+m.pedidosYaMonto));

    let rightY=boxTop+36;
    pdf.setFont('helvetica','bold');pdf.text(m.conteoFisico ? 'Caja física' : 'Movimiento de efectivo',rightColX,rightY);
    pdf.setFont('helvetica','normal');rightY+=GAP;
    drawLine(rightColX,rightY,'Efectivo inicial',toMoney(m.inicial));rightY+=GAP;
    drawLine(rightColX,rightY,'Ventas efectivo',toMoney(m.cierreEf));rightY+=GAP;
    drawLine(rightColX,rightY,'Gastos en efectivo',toMoney(m.gastos));rightY+=GAP;
    drawLine(rightColX,rightY,'Pagos efectivo',toMoney(m.pagos));rightY+=GAP;
    // Sin datos del sistema aún no hay esperado ni diferencia (pendiente de revisión).
    drawLine(rightColX,rightY,m.conteoFisico?'Efectivo esperado':'Efectivo resultante',m.esperado==null?'Pendiente':toMoney(m.esperado));rightY+=GAP;
    if (m.conteoFisico) { drawLine(rightColX,rightY,'Efectivo contado',toMoney(m.totalArqueoEfectivo));rightY+=GAP; }
    if (m.fondo) { drawLine(rightColX,rightY,'Fondo dejado en caja',toMoney(m.dejaEnCaja));rightY+=GAP; }

    if (m.conteoFisico) {
      const diffLabel = m.diffEf == null ? 'Diferencia de caja' : m.diffEf >= 0 ? 'Sobrante' : 'Faltante';
      drawLine(rightColX, rightY, diffLabel, m.diffEf == null ? 'Pendiente' : toMoney(Math.abs(m.diffEf))); rightY += GAP;
    }

    if (m.faltantePagado > 0) {
      drawLine(rightColX, rightY, 'Faltante pagado', toMoney(m.faltantePagado)); rightY += GAP;
    }

    const contentBottom = Math.max(leftY, rightY);
    const totalDepY = contentBottom + 20;
    const depColor = m.totalDepositar < 0 ? [183, 28, 28] : [27, 94, 32];
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...depColor);
    const centerX = width / 2;
    pdf.text(`Total a depositar: ${toMoney(m.totalDepositar)}`, centerX, totalDepY, { align: 'center' });
    pdf.setTextColor(33, 37, 41);

    const boxHeight = (totalDepY - boxTop) + 14;
    pdf.setDrawColor(230, 236, 240);
    pdf.roundedRect(40, boxTop, width - 80, boxHeight, 4, 4);

    y = boxTop + boxHeight + 12;
  }

  // Comentario (nueva página si hace falta)
  if (y > pageHeight - bottomMargin) {
    pdf.addPage();
    y = 40;
  }

  const comentarioPlano = (c.comentario || '').toString().trim();
  if (comentarioPlano) {
    autoTable(pdf, {
      startY: y,
      head: [['Comentario']],
      body: [[comentarioPlano]],
      styles: { fontSize: 10, cellPadding: 6, lineWidth: 0.2, lineColor: [230, 236, 240] },
      headStyles: { fillColor: [69, 90, 100], textColor: 255 },
      theme: 'grid',
    });
  }
};
